import React, { FC, useEffect, useState } from 'react'
import api, { IFloorMapSpace, INeighborhood, ISpaceDetails, ITag, IWorkplaceGroup } from '@api'
import { Avatar, Flex, Toaster } from '@ui'
import css from './spaceInfo.module.scss'
import { DateUtil } from '@utils/dateUtil'
import MeasurementInput from 'components/AdminFloorPlan/components/MeasurementInput'
import { Toggle, Label, TextField, Button } from '@runeffective/evo-design-system'
import { isEqual } from 'lodash'
import MultiAssetsSelect from '@ui/selects/MultiAssetsSelect'
import NeighborhoodSelect from '@ui/selects/NeighborhoodSelect'
import MultiWorkGroupSelect from '@ui/selects/MultiWorkplaceGroupSelect'
import { ApolloError } from 'apollo-client'
import { getRoleLabel } from '@ui/selects/RoleSelect'
import { useFloor } from '@providers/mapProvider'
import { isNeighborhood, isSecondary, isShared } from '@utils'

type SpaceInfoProps = {
  space: IFloorMapSpace
  assignedEmployee: any
  assignmentPlanId: string
  updateData: (space: ISpaceDetails) => void
}

export interface IFormEmployee {
  isRemote: boolean
  noDeskNeed: boolean
}

const SpaceInfo: FC<SpaceInfoProps> = ({
  space,
  assignedEmployee,
  assignmentPlanId,
  updateData,
}) => {
  const startDate = space.spaceAssignment?.employee?.startDate
  const endDate = space.spaceAssignment?.employee?.endDate
  const [employee, setEmployee] = useState<any>(assignedEmployee)
  const [updatedEmployee, setUpdatedEmployee] = useState<{
    tagNames: any
    workplaceGroupIds: any
  }>({
    tagNames: [],
    workplaceGroupIds: [],
  })
  const [dirty, setDirty] = useState(false)
  const floor = useFloor()
  const onValueChange = ({
    name,
    value,
  }: {
    name: string
    value: INeighborhood[] | ITag[] | IWorkplaceGroup[] | boolean | string | undefined
  }) => {
    setEmployee({ ...employee, [name]: value })
    if (name !== 'tags' && name !== 'workplaceGroups' && name !== 'neighborhoods')
      setUpdatedEmployee({ ...updatedEmployee, [name]: value })
  }

  const unseatEmployee = () => {
    api.space
      .seat({
        assignmentPlanId: assignmentPlanId,
        employeeId: employee.id,
        fromSpaceId: space.id,
        moveType: 'move',
        toSpaceId: null,
      })
      .then((response) => {
        updateData({
          ...space,
          spaceAssignment: response.fromSpace.spaceAssignment,
        } as ISpaceDetails)
        Toaster.success({
          message: 'Employee was unseated',
        })
        floor.reloadMap?.()
      })
      .catch((error: ApolloError) => {
        Toaster.fail({ message: error.message, error: error })
      })
  }

  const addRemoveNeighborhoods = (neighborhood: any, deletedItem?: any) => {
    if (deletedItem.status === 'remove') {
      api.neighborhood.removeEmployees(deletedItem.id, [employee.id]).then((nh) => {
        onValueChange({ name: 'neighborhoods', value: neighborhood })
        Toaster.success({
          message: `${employee.name} was removed from neighborhood ${nh.name}`,
        })
        floor.reloadMap?.()
      })
    } else {
      api.neighborhood.addEmployees(neighborhood[0].id, [employee.id]).then((neighborhood) => {
        !isSecondary(space.usageType)
          ? updateData({
              ...space,
              spaceAssignment: { employee: null },
            } as ISpaceDetails)
          : onValueChange({
              name: 'neighborhoods',
              value: [...employee.neighborhoods, neighborhood],
            })
        Toaster.success({
          message: `${employee.name} was associated to neighborhood ${neighborhood.name}`,
        })
        floor.reloadMap?.()
      })
    }
  }

  useEffect(() => {
    const isEQual = isEqual(employee, space?.spaceAssignment?.employee)
    setDirty(!isEQual)
  }, [employee, space.spaceAssignment.employee])

  return (
    <>
      <div>
        <Flex align="flex-start" className={css.avatar}>
          <Avatar name={employee.name} assets={employee.assets} size={Avatar.Size.XMD} />
          <div className={'truncate'}>
            <p style={{ wordBreak: 'break-all', whiteSpace: 'normal' }} className={css.avatar_name}>
              {employee.name}
            </p>
            <p className={css.avatar_department}>{employee?.dept.name}</p>
          </div>
        </Flex>
        <Flex
          align="flex-start"
          justify="space-between"
          direction={isShared(space.usageType) ? 'row' : 'column'}
          className={css.row}
        >
          <Field space={space} width="50%" title="Department" value={employee.dept.name} />
          <div style={{ margin: '5px 0' }} />
          <Field space={space} width="50%" title="Status" value={employee.employmentType} />
        </Flex>
        <Flex
          align="flex-start"
          justify="space-between"
          direction={isShared(space.usageType) ? 'row' : 'column'}
          className={css.row}
        >
          <Field space={space} width="50%" title="Manager" value={employee.manager?.name} />
          <div style={{ margin: '5px 0' }} />
          <Field space={space} width="50%" title="Phone" value={employee.phone} />
        </Flex>

        <Flex
          align="flex-start"
          justify="space-between"
          direction={isShared(space.usageType) ? 'row' : 'column'}
          className={css.row}
        >
          <Field space={space} width="50%" title="Email" value={employee.email} />
          <div style={{ margin: '5px 0' }} />
          <Field
            space={space}
            width="50%"
            title="HR Location"
            value={employee.geoLocation?.location}
          />
        </Flex>
        <Flex align="flex-start" justify="space-between" className={css.row}>
          <Field
            space={space}
            width="50%"
            title="Start date"
            value={startDate ? DateUtil(startDate).format(DATE_FORMAT) : null}
          />
         <div style={{ margin: '5px 0' }} />
          <Field
            space={space}
            width="50%"
            title="End date"
            value={endDate ? DateUtil(endDate).format(DATE_FORMAT) : null}
          />
        </Flex>
        <Flex
          align="flex-start"
          justify="space-between"
          direction={isShared(space.usageType) ? 'row' : 'column'}
          className={css.row}
        >
          <Field space={space} width="50%" title="External ID" value={employee?.externalId} />
          <div style={{ margin: '5px 0' }} />
          <Field space={space} width="50%" title="Badge" value={employee?.badge} />
        </Flex>

        <Flex
          align="flex-start"
          justify="space-between"
          direction={isShared(space.usageType) ? 'row' : 'column'}
          className={css.row}
        >
          <Field space={space} width="50%" title="Cost center" value={employee?.costCenter?.name} />
          <Flex align="flex-start" className={css.row}>
            <MeasurementInput
              disabled={true}
              value={employee?.measurements as number}
              onChange={() => {}}
            />
          </Flex>
        </Flex>
        <Flex
          align="flex-start"
          justify="space-between"
          direction={isShared(space.usageType) ? 'row' : 'column'}
          className={css.row}
        >
          <Field space={space} width="50%" title="Employee Id" value={employee?.externalId} />
          <Flex
            align="flex-start"
            justify={isShared(space.usageType) ? 'flex-start' : 'space-between'}
            style={{ width: isShared(space.usageType) ? '50%' : '100%' }}
            className={css.row}
          >
            <Flex align="flex-start" justify="flex-start" direction="column">
              <Label label="Remote" className={css.field_title} />
              <Toggle
                isChecked={employee?.isRemote}
                onChange={(checked: boolean) => {
                  onValueChange({ name: 'isRemote', value: checked })
                }}
                size="default"
              />
            </Flex>
            <Flex
              style={{ marginLeft: '20px' }}
              align="flex-start"
              justify="flex-start"
              direction="column"
            >
              <Label className={css.field_title} label="No desk needed" />
              <Toggle
                isChecked={employee?.noDeskNeeded}
                onChange={(checked: boolean) => {
                  onValueChange({ name: 'noDeskNeeded', value: checked })
                }}
                size="default"
              />
            </Flex>
          </Flex>
        </Flex>

        <Flex
          align="flex-start"
          justify="space-between"
          className={css.row}
          direction={isShared(space.usageType) ? 'row' : 'column'}
        >
          <Field space={space} width="50%" title="Role" value={getRoleLabel(employee?.role)} />
        </Flex>
        <Flex
          align="flex-start"
          justify="space-between"
          className={css.row}
          direction={isShared(space.usageType) ? 'row' : 'column'}
        >
          <div className={css.row} style={{ width: isShared(space.usageType) ? '50%' : '100%' }}>
            <div>
              <Label label="Employee assets/needs" />
              <MultiAssetsSelect
                values={employee?.tags}
                onChange={(tags) => {
                  onValueChange({ name: 'tags', value: tags })
                  setUpdatedEmployee({
                    ...updatedEmployee,
                    tagNames: tags?.map((tag: ITag) => tag.name),
                  })
                }}
                onClear={() => {
                  onValueChange({ name: 'tags', value: [] })
                  setUpdatedEmployee({
                    ...updatedEmployee,
                    tagNames: [],
                  })
                }}
                onCreate={(item: string) => {
                  onValueChange({
                    name: 'tags',
                    value: [...employee.tags, { id: '', name: item }],
                  }) as unknown as Promise<ITag>
                  setUpdatedEmployee({
                    ...updatedEmployee,
                    tagNames: [...(updatedEmployee?.tagNames as string[]), item],
                  })
                }}
              />
            </div>
          </div>
          <div className={css.row} style={{ width: isShared(space.usageType) ? '50%' : '100%' }}>
            <div>
              <Label label="Note" />
              <TextField
                style={{ width: isShared(space.usageType) ? '300px' : undefined }}
                size="small"
                placeholder="Note"
                value={employee?.note}
                onChange={(event) => {
                  onValueChange({ name: 'note', value: event })
                }}
              />
            </div>
          </div>
        </Flex>
        <Flex
          align="flex-start"
          justify="space-between"
          className={css.row}
          direction={isShared(space.usageType) ? 'row' : 'column'}
        >
        <div className={css.row} style={{ width: isShared(space.usageType) ? '50%' : '100%' }}>
          <div>
            <Label label="Workplace groups" />
            <MultiWorkGroupSelect
              values={employee?.workplaceGroups}
              onChange={(wpg) => {
                onValueChange({ name: 'workplaceGroups', value: wpg })
                setUpdatedEmployee({
                  ...updatedEmployee,
                  workplaceGroupIds: wpg?.map((wpg: IWorkplaceGroup) => wpg?.id),
                })
              }}
              onClear={() => {
                onValueChange({ name: 'workplaceGroups', value: [] })
                setUpdatedEmployee({
                  ...updatedEmployee,
                  workplaceGroupIds: [],
                })
              }}
              onCreate={(item: string) =>
                api.workplaceGroup.create({ name: item }).then(
                  (workplaceGroup) =>
                    onValueChange({
                      name: 'workplaceGroups',
                      value: [...employee.workplaceGroups, workplaceGroup],
                    }) as unknown as Promise<IWorkplaceGroup>
                )
              }
            />
          </div>
        </div>
        <div className={css.row} style={{ width: isShared(space.usageType) ? '50%' : '100%' }}>
            <div>
              <Label label="Available neighborhoods" />
              <NeighborhoodSelect
                values={employee?.neighborhoods}
                onChange={(neighborhood, deletedItem) => {
                  addRemoveNeighborhoods(neighborhood, deletedItem)
                }}
                onClear={() => {
                  addRemoveNeighborhoods([])
                }}
                onCreate={(item: string) =>
                  api.neighborhood
                    .create({ floorId: floor.apId, name: item })
                    .then(
                      (neighborhood) =>
                        addRemoveNeighborhoods(neighborhood) as unknown as Promise<INeighborhood>
                    )
                    .catch(
                      (error) =>
                        Toaster.fail({
                          error: error,
                          message: error.message,
                        }) as unknown as Promise<INeighborhood>
                    )
                }
              />
            </div>
          </div>
          </Flex>
        {!isShared(space.usageType) && !isNeighborhood(space.usageType) && (
          <div className={css.row}>
            <Button
              className={css.centerText}
              style={{ width: '100%' }}
              layout="danger"
              onClick={() => {
                unseatEmployee()
              }}
              size="small"
            >
              Unassign
            </Button>
          </div>
        )}
        <Flex
          className={css.row}
          align="flex-start"
          justify={isShared(space.usageType) ? 'flex-end' : 'space-between'}
        >
          <div>
            <Button
              onClick={() => setEmployee(space.spaceAssignment.employee)}
              layout="secondary"
              size="small"
            >
              Cancel
            </Button>
          </div>
          <div>
            <Button
              style={{ marginLeft: '10px' }}
              disabled={!dirty}
              onClick={() => {
                api.employee
                  .update({
                    id: employee?.id,
                    ...updatedEmployee,
                  })
                  .then(() => {
                    setUpdatedEmployee({ tagNames: [], workplaceGroupIds: [] })
                    Toaster.success({
                      message: 'Details updated successfully',
                    })
                  })
                  .catch((error: ApolloError) => {
                    Toaster.fail({
                      message:
                        "Something went wrong and we can't update the space details. Try again at a later time",
                      error: error,
                    })
                  })
              }}
              layout="primary"
              size="small"
            >
              Update
            </Button>
          </div>
        </Flex>
      </div>
    </>
  )
}

type FieldProps = {
  title: string
  value: string | null | undefined | number
  width?: string
  space?: any
}

export const Field: FC<FieldProps> = ({ title, value, width, space }) => (
  <div
    style={{ width: width, marginBottom: isShared(space.usageType) ? '0px' : '5px' }}
    className={css.field_wrapper}
  >
    <p className={css.field_title}>{title}</p>
    <p className={css.field_value}>{value || 'N/A'}</p>
  </div>
)

export default SpaceInfo

const DATE_FORMAT = 'MMM d, yyyy'
